import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { InsightActionPreview, RunDetail, RunListRow } from '../models/insightAction.types'
import { isTerminalRunStatus } from '../models/insightAction.types'
import { previewKey } from '../models/insightActionPanel.types'
import type {
  ActStep,
  PanelMode,
  PreviewEntry,
  RunState,
  StaleRecovery,
} from '../models/insightActionPanel.types'

const EMPTY_ENTRY: PreviewEntry = { preview: null, loading: false, errorCode: '' }

export const useInsightActionStore = defineStore('insightActions', () => {
  const previews = ref<Record<string, PreviewEntry>>({})
  const runCounts = ref<Record<string, number>>({})
  const activeKey = ref('')
  const insightId = ref<number | null>(null)

  const selection = ref<Set<string>>(new Set())
  const groupKey = ref('')
  const channelFilter = ref<string | null>(null)
  const onlyApplicable = ref(false)

  const mode = ref<PanelMode>('read')
  const step = ref<ActStep>('select')
  const panelOpen = ref(false)
  const idempotencyKey = ref('')

  const runs = ref<Record<string, RunState>>({})
  const applying = ref(false)
  const applyErrorCode = ref('')
  const staleRecovery = ref<StaleRecovery | null>(null)

  const history = ref<RunListRow[]>([])
  const historyLoading = ref(false)
  const historyLoaded = ref(false)
  const runDetails = ref<Record<string, RunDetail>>({})
  const expandedRuns = ref<string[]>([])
  const reverting = ref<string[]>([])
  const revertQueued = ref<Record<string, number>>({})
  const revertErrors = ref<Record<string, string>>({})

  const entry = computed<PreviewEntry>(() => previews.value[activeKey.value] ?? EMPTY_ENTRY)
  const preview = computed(() => entry.value.preview)
  const groups = computed(() => preview.value?.groups ?? [])
  const activeGroup = computed(() => groups.value.find((g) => g.key === groupKey.value) ?? null)
  const run = computed<RunState | null>(() => runs.value[activeKey.value] ?? null)
  const isRunTerminal = computed(() => isTerminalRunStatus(run.value?.status))
  const selectedCount = computed(() => selection.value.size)
  const historyRunCount = computed(
    () => runCounts.value[activeKey.value] ?? preview.value?.runCount ?? 0,
  )
  const executionEnabled = computed(() => preview.value?.executionEnabled)
  const hasWarmingGroup = computed(() => groups.value.some((g) => g.eligibilityState !== 'ready'))

  const runProgress = computed<{ done: number; total: number } | null>(() => {
    const current = run.value
    if (!current || isTerminalRunStatus(current.status)) return null
    const { succeeded, failed, awaitingApproval, skipped } = current.counts
    return { done: succeeded + failed + awaitingApproval + skipped, total: current.totalItems }
  })

  const runsByInsight = computed<Record<string, { done: number; total: number }>>(() => {
    const out: Record<string, { done: number; total: number }> = {}
    for (const value of Object.values(runs.value)) {
      if (isTerminalRunStatus(value.status)) continue
      const { succeeded, failed, awaitingApproval, skipped } = value.counts
      out[String(value.insightId)] = {
        done: succeeded + failed + awaitingApproval + skipped,
        total: value.totalItems,
      }
    }
    return out
  })

  function setPreviewLoading(key: string): void {
    previews.value[key] = {
      preview: previews.value[key]?.preview ?? null,
      loading: true,
      errorCode: '',
    }
  }

  function setPreview(key: string, value: InsightActionPreview): void {
    previews.value[key] = { preview: value, loading: false, errorCode: '' }
  }

  function setPreviewError(key: string, code: string): void {
    previews.value[key] = { preview: null, loading: false, errorCode: code }
  }

  function cached(key: string): PreviewEntry | undefined {
    return previews.value[key]
  }

  function bindInsight(key: string, id: number): void {
    if (key !== activeKey.value) {
      activeKey.value = key
      selection.value = new Set()
      groupKey.value = ''
      applyErrorCode.value = ''
      staleRecovery.value = null
      revertErrors.value = {}
      revertQueued.value = {}
      expandedRuns.value = []
    }
    insightId.value = id
    panelOpen.value = true
  }

  function closePanel(): void {
    panelOpen.value = false
    mode.value = 'read'
  }

  function setMode(next: PanelMode): void {
    mode.value = next
    if (next === 'history') historyLoaded.value = false
    if (next === 'act') ensureGroup()
    if (next === 'act' && step.value === 'result') step.value = 'select'
  }

  function setStep(next: ActStep): void {
    step.value = next
  }

  function setIdempotencyKey(value: string): void {
    idempotencyKey.value = value
  }

  function setGroup(next: string): void {
    if (next === groupKey.value) return
    groupKey.value = next
    selection.value = new Set()
    channelFilter.value = null
  }

  function ensureGroup(): void {
    if (!groups.value.some((g) => g.key === groupKey.value)) {
      groupKey.value = groups.value[0]?.key ?? ''
    }
  }

  function setChannelFilter(channel: string | null): void {
    channelFilter.value = channelFilter.value === channel ? null : channel
  }

  function setOnlyApplicable(value: boolean): void {
    onlyApplicable.value = value
  }

  function toggleRow(rowKey: string): void {
    const next = new Set(selection.value)
    if (!next.delete(rowKey)) next.add(rowKey)
    selection.value = next
  }

  function setSelection(rowKeys: string[]): void {
    selection.value = new Set(rowKeys)
  }

  function clearSelection(): void {
    selection.value = new Set()
  }

  function setStaleRecovery(value: StaleRecovery | null): void {
    staleRecovery.value = value
  }

  function setApplying(value: boolean): void {
    applying.value = value
    if (value) {
      applyErrorCode.value = ''
      staleRecovery.value = null
    }
  }

  function setApplyError(code: string): void {
    applyErrorCode.value = code
  }

  function setRun(key: string, state: RunState): void {
    runs.value[key] = state
  }

  function dropRun(key: string): void {
    delete runs.value[key]
  }

  function setHistoryLoading(value: boolean): void {
    historyLoading.value = value
  }

  function setHistory(key: string, rows: RunListRow[], total: number): void {
    history.value = rows
    runCounts.value[key] = total
    historyLoaded.value = true
    historyLoading.value = false
  }

  function setRunDetail(runId: string, detail: RunDetail): void {
    runDetails.value[runId] = detail
  }

  function toggleExpanded(runId: string): boolean {
    if (expandedRuns.value.includes(runId)) {
      expandedRuns.value = expandedRuns.value.filter((id) => id !== runId)
      return false
    }
    expandedRuns.value = [...expandedRuns.value, runId]
    return true
  }

  function setReverting(token: string, busy: boolean): void {
    reverting.value = busy
      ? [...reverting.value, token]
      : reverting.value.filter((entryToken) => entryToken !== token)
    if (busy) delete revertErrors.value[token]
  }

  function setRevertError(token: string, code: string): void {
    revertErrors.value[token] = code
  }

  function setRevertQueued(runId: string, count: number): void {
    revertQueued.value[runId] = count
  }

  function reset(): void {
    previews.value = {}
    runCounts.value = {}
    activeKey.value = ''
    insightId.value = null
    selection.value = new Set()
    groupKey.value = ''
    channelFilter.value = null
    onlyApplicable.value = false
    mode.value = 'read'
    step.value = 'select'
    panelOpen.value = false
    idempotencyKey.value = ''
    runs.value = {}
    applying.value = false
    applyErrorCode.value = ''
    staleRecovery.value = null
    history.value = []
    historyLoading.value = false
    historyLoaded.value = false
    runDetails.value = {}
    expandedRuns.value = []
    reverting.value = []
    revertQueued.value = {}
    revertErrors.value = {}
  }

  return {
    previews,
    runCounts,
    activeKey,
    insightId,
    selection,
    groupKey,
    channelFilter,
    onlyApplicable,
    mode,
    step,
    panelOpen,
    idempotencyKey,
    runs,
    applying,
    applyErrorCode,
    staleRecovery,
    history,
    historyLoading,
    historyLoaded,
    runDetails,
    expandedRuns,
    reverting,
    revertQueued,
    revertErrors,
    entry,
    preview,
    groups,
    activeGroup,
    run,
    isRunTerminal,
    selectedCount,
    historyRunCount,
    executionEnabled,
    hasWarmingGroup,
    runProgress,
    runsByInsight,
    setPreviewLoading,
    setPreview,
    setPreviewError,
    cached,
    bindInsight,
    closePanel,
    setMode,
    setStep,
    setIdempotencyKey,
    setGroup,
    ensureGroup,
    setChannelFilter,
    setOnlyApplicable,
    toggleRow,
    setSelection,
    clearSelection,
    setStaleRecovery,
    setApplying,
    setApplyError,
    setRun,
    dropRun,
    setHistoryLoading,
    setHistory,
    setRunDetail,
    toggleExpanded,
    setReverting,
    setRevertError,
    setRevertQueued,
    reset,
  }
})

export type InsightActionStore = ReturnType<typeof useInsightActionStore>
export { previewKey }
export type { ActStep, PanelMode, PreviewEntry, RunState, StaleRecovery }
